import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import {
  errorMessage,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  orderStepper,
  ProductType,
  WeightType,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { CustomerOrderService } from '@services/CustomerService';
import {
  OrderSpeciesData,
  OrderServiceProps,
  PackageMaster,
  SpeciesMaster,
  OrderServices,
  AddonsListProps,
  MasterProduct,
  NewOrderInitiationProps,
} from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import {
  calculateTotalPrice,
  formatDate,
  getSumOfAddOns,
  getSumOfPackage,
} from '@services/OrderCreationService';
import { useFetchStateName } from '@hooks/useFetchStateName';

export const SpeciesSummary: React.FC = () => {
  const navigate = useNavigate();
  const { getStateName } = useFetchStateName();
  const { showToastMessage } = useToast();
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [orderData, setOrderData] = useState<OrderSpeciesData>();
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [currentSpecies, setCurrentSpecies] = useState<SpeciesMaster | null>(
    null,
  );
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [packageMasters, setPackageMasters] = useState<PackageMaster[]>([]);
  const [deposits, setDeposits] = useState<Record<number, string>>({});

  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  const [depositSelections, setDepositSelections] = useState<
    Record<number, boolean>
  >({});
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const customerId = CustomerOrderService.getCustomerId();

  /**
   * Initializes the component state by retrieving and parsing the current customer's order data.
   *
   * This effect runs once on component mount and:
   * - Retrieves parsed order data and the customer ID from the `CustomerOrderService`.
   * - Gets the index of the currently customizing species from local storage.
   * - Extracts the relevant species data and sets it in component state using `setOrderData`.
   * - If the species ID exists, it:
   *    - Fetches all associated master data (services, products, packaging, etc.).
   *    - Extracts and sets additional customization values like `additionalNotes`, `advancePayment`, and `depositAmount`.
   */
  useEffect(() => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    const currentCustomizingIndex =
      CustomerOrderService.getcurrentCustomizingId();
    const currentCustomerOrderData = parsedOrderData[customerId];
    const currentCustomerData =
      currentCustomerOrderData.species[currentCustomizingIndex];
    setOrderData(currentCustomerData);
    if (currentCustomerOrderData.speciesId) {
      fetchAddons();
      fetchMasterPackages();
      fetchMasterProducts();
      fetchMasters(currentCustomerOrderData.speciesId);
      const { additionalNotes } = currentCustomerData;
      setAdditionalNotes(additionalNotes ?? '');

      // Get the deposit amount of the service if there's any
      const selections: Record<number, boolean> = {};
      const depositValues: Record<number, string> = {};

      currentCustomerData.services.forEach((s: any) => {
        if (s.depositAmount !== undefined) {
          selections[s.serviceId] = true; // To check the checkbox of the service
          depositValues[s.serviceId] = s.depositAmount;
        }
      });

      setDepositSelections(selections);
      setDeposits(depositValues);
    }
  }, []);

  /**
   * Fetches all master data related to a specific species and updates state accordingly.
   *
   * This function retrieves the following master data in parallel:
   * - Services
   * - Species
   *
   * Once the data is fetched, it sets the relevant state variables:
   * - `setServices` with services data.
   * - `setCurrentSpecies` with the species that matches the provided `speciesId`.
   *
   * @param {number} speciesId - The ID of the species to fetch related master data for.
   *
   * @returns {Promise<any>} A promise that resolves when all data is fetched and state is updated.
   */
  const fetchMasterData = async (
    speciesId: number,
    type: number,
  ): Promise<any> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId,
        masterDataType: type,
      });
      return response.data?.data?.masterData;
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of master products for species customization.
   *
   * This function makes a POST request to the `FETCH_SPECIES_MASTERDATA` API
   * with `masterDataType` set to 4, which is used to retrieve product master data.
   * The response is then stored in the component state using `setMasterProducts`.
   *
   * In case of an error, a toast message is shown and the error is rethrown.
   *
   * @returns {Promise<void>} A promise that resolves when the data is successfully fetched and set.
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      const masterData = response.data.data.masterData;
      setMasterProducts(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of available add-ons for product customization.
   *
   * This function sends a POST request to the `FETCH_ADDONS` API endpoint.
   * Upon successful response, it sets the retrieved add-ons into the component state
   * using `setAddons`. The expected response contains an `addOns` array.
   *
   * In case of a request failure, an error toast is displayed and the error is rethrown
   * for further handling.
   *
   * @returns {Promise<void>} A promise that resolves when the add-ons are successfully fetched and set.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master data for product packaging options.
   *
   * Sends a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint with `masterDataType` set to 6,
   * which corresponds to packaging data. On success, it extracts the `packageType` array from the
   * response and updates the component state via `setPackageMasters`.
   *
   * Displays an error toast and rethrows the error if the request fails.
   *
   * @returns {Promise<void>} A promise that resolves once the packaging data has been successfully fetched and stored.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setPackageMasters(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches all master data related to a given species and updates component state accordingly.
   *
   * This function:
   * - Uses `Promise.all` to concurrently fetch master data for:
   *    - Services (typeId: 3)
   *    - Species list (typeId: 1)
   * - Updates the local state with:
   *    - List of available services
   * - Attempts to find and set the current species from the fetched species data.
   *
   * @param {number} speciesId - The ID of the species for which master data is to be fetched.
   */
  const fetchMasters = async (speciesId: number): Promise<void> => {
    try {
      const [servicesData, speciesData] = await Promise.all([
        fetchMasterData(speciesId, OrderCreationMasterData.SERVICE_MASTER_DATA),
        fetchMasterData(speciesId, OrderCreationMasterData.SPECIES_MASTER_DATA),
      ]);
      setServices(servicesData?.services ?? []);
      const foundSpecies = speciesData?.find(
        (species: SpeciesMaster) => species.id === speciesId,
      );
      setCurrentSpecies(foundSpecies ?? null);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Finalizes the customization of a specific species in the customer's order.
   *
   * This function retrieves the current parsed customer order data, locates the species
   * being customized based on the `currentCustomizingId` stored in `localStorage`,
   * updates its `additionalNotes`, `advancePayment`, and `depositAmount` fields,
   * then saves the modified order data back to storage and navigates the user to
   * the order summary screen.
   *
   * Dependencies:
   * - CustomerOrderService.getParsedOrderData(): retrieves stored order data.
   * - CustomerOrderService.getCustomerId(): retrieves current customer ID.
   * - CustomerOrderService.setParsedOrderData(data): saves updated order data.
   * - localStorage: used to track the currently customized species index.
   * - navigate(): redirects user to order summary.
   *
   * Side Effects:
   * - Modifies localStorage-stored order data.
   * - Triggers a route change to the order summary view.
   */
  const handleCompleteCustomization = (): void => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerId = CustomerOrderService.getCustomerId();

    const currentCustomerOrderData = parsedOrderData[customerId];
    const currentCustomizingIndex =
      CustomerOrderService.getcurrentCustomizingId();
    const speciesArray = currentCustomerOrderData.species;
    // Update the services with deposite amount if any
    const updatedServices =
      speciesArray[currentCustomizingIndex].services?.map(
        (service: { serviceId: number }) => {
          const isSelected = depositSelections[service.serviceId];
          return {
            ...service,
            ...(isSelected
              ? { depositAmount: Number(deposits[service.serviceId]) }
              : {}),
          };
        },
      ) ?? [];

    const updatedSpeciesData = {
      ...speciesArray[currentCustomizingIndex],
      additionalNotes,
      services: updatedServices,
      // Set isCustomizationCompleted as 1 to mark the completion of the current species.
      isCustomizationCompleted: 1,
    };

    speciesArray[currentCustomizingIndex] = updatedSpeciesData;
    currentCustomerOrderData.species = speciesArray;
    parsedOrderData[customerId] = currentCustomerOrderData;

    CustomerOrderService.setParsedOrderData(parsedOrderData);
    navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION);
  };

  /**
   * Toggles the deposit selection status for a specific service.
   * * This function updates the `depositSelections` state by flipping the boolean
   * value associated with the provided `serviceId`. If the ID doesn't exist
   * in the map yet, it will be initialized as `true`.
   * * @param {number} serviceId - The unique identifier of the service being toggled.
   * @returns {void}
   */
  const handleDepositToggle = (serviceId: number) => {
    setDepositSelections((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  return (
    <div className="flex h-screen w-screen">
      <SideNav active={2} />
      <div className="flex-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        {/* Order Breadcrumb */}
        <div className="px-2">
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>

        {/* Main Content */}
        <div className="p-4">
          <OrderProfileCard />

          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={6} />

          {/* Summary Section */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Summary</h2>
              <button
                onClick={() =>
                  navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION)
                }
                type="button"
                className="text-primary font-semibold"
              >
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
            </div>
            {/* Check-in */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold ml-2">Check-In</h3>
              <button
                onClick={() => navigate(FrontendRoutes.CREATE_ORDER_CHECKIN)}
                type="button"
                className="text-primary font-semibold"
              >
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
            </div>
            <div className="mt-4 p-3 rounded-lg shadow-md bg-quaternary font-medium">
              {orderData && (
                <>
                  {/* Species State and License Number */}
                  {orderData.checkIn.huntingLicenceId && (
                    <div className="bg-white p-4 rounded-md border mb-2">
                      <div className="flex w-full justify-start">
                        <div className="w-1/2">
                          <span className="block mb-2 text-[#000A1666] font-medium">
                            Species State
                          </span>
                          <span>
                            {getStateName(
                              Number(orderData.checkIn.harvestStateId),
                            )}
                          </span>
                        </div>
                        <div className="w-1/2 ">
                          <span className="block mb-2 text-[#000A1666] font-medium">
                            License Number
                          </span>
                          <div className="flex justify-between">
                            <span>{orderData.checkIn.licenseNumber}</span>
                            <span>
                              Expiry:{' '}
                              {formatDate(
                                customerDetails?.HuntingLicences?.find(
                                  (license) =>
                                    license.id ===
                                    orderData.checkIn.huntingLicenceId,
                                )?.expiryDate || '',
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Permit, CITES and Confirmation Number */}
                  {(orderData.checkIn.permitNumber ||
                    orderData.checkIn.citesNumber ||
                    orderData.checkIn.conformationNumber) && (
                    <div className="bg-white p-4 rounded-md border mb-2">
                      <div className="flex w-full justify-start">
                        {orderData.checkIn.permitNumber && (
                          <div className="w-1/2">
                            <span className="block mb-2 text-[#000A1666] font-medium">
                              Permit Number
                            </span>
                            <span>{orderData.checkIn.permitNumber}</span>
                          </div>
                        )}
                        {orderData.checkIn.citesNumber && (
                          <div className="w-1/2">
                            <span className="block mb-2 text-[#000A1666] font-medium">
                              CITES Number
                            </span>
                            <span>{orderData.checkIn.citesNumber}</span>
                          </div>
                        )}
                      </div>
                      {orderData.checkIn.conformationNumber && (
                        <div className="w-full mt-3">
                          <span className="block mb-2 text-[#000A1666] font-medium">
                            Confirmation Number
                          </span>
                          <span>{orderData.checkIn.conformationNumber}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Length and Weight */}
                  {(orderData.checkIn.feet || orderData.checkIn.weight) && (
                    <div className="bg-white p-4 rounded-md border mb-2">
                      <div className="flex w-full justify-start">
                        {orderData.checkIn.feet && (
                          <div className="w-1/2">
                            <span className="block mb-2 text-[#000A1666] font-medium">
                              Length
                            </span>
                            <span>
                              {orderData.checkIn.feet} Feet{' '}
                              {orderData.checkIn.inches} Inches
                            </span>
                          </div>
                        )}
                        {orderData.checkIn.weight && (
                          <div className="w-1/2">
                            <span className="block mb-2 text-[#000A1666] font-medium">
                              Weight
                            </span>
                            <span>{orderData.checkIn.weight}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
              {orderData?.checkIn?.notes && (
                <div className="bg-white p-4 rounded-md border mb-2 mt-4">
                  <h4 className="font-medium mb-2">Check - in notes:</h4>
                  <div className="space-y-2">
                    <span className="text-[#43474E]">
                      {orderData.checkIn.notes}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Services */}
          {!orderData?.services?.length ? (
            <div className="mt-3">
              <h3 className="text-lg font-semibold ml-2 mb-4">Services</h3>
              <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                  No service selected! Go back and add services
                </h3>
                <div className="relative">
                  <button
                    onClick={() =>
                      navigate(FrontendRoutes.CREATE_ORDER_SERVICE)
                    }
                    type="button"
                    className="text-primary font-bold"
                  >
                    Select Service
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold ml-2">Services</h3>
                <button
                  type="button"
                  onClick={() => navigate(FrontendRoutes.CREATE_ORDER_SERVICE)}
                  className="text-primary font-semibold"
                >
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
              </div>
              <div className="mt-3">
                {orderData?.services?.map((service: OrderServices) => {
                  const master = services.find(
                    (m) => m.id === Number(service.serviceId),
                  );
                  if (!master) return null;
                  return (
                    <div
                      key={service.serviceId}
                      className="bg-quaternary mb-4 p-4 rounded-md "
                    >
                      <div className="flex items-center gap-4">
                        <img
                          src={currentSpecies?.imageUrl}
                          alt={currentSpecies?.name}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                        <div className="flex-1">
                          <p className="text-gray-900 font-semibold">
                            {master.name}
                          </p>
                          <div className="bg-white p-2 mt-2 rounded-md border text-gray-600 text-sm">
                            Total Price to be calculated after processing
                          </div>
                        </div>
                        <div>
                          <p className="text-gray-900 font-semibold">
                            ${master.rate}/{master.unit}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white p-2 mt-4 rounded-md">
                        <div className="flex items-center justify-between text-gray-600 text-sm">
                          <span>Collect Deposit</span>
                          <input
                            type="checkbox"
                            checked={!!depositSelections[service.serviceId]}
                            onChange={() =>
                              handleDepositToggle(service.serviceId)
                            }
                            className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          />
                        </div>

                        {depositSelections[service.serviceId] && (
                          <div className="flex items-center justify-between mt-4 text-gray-600 text-sm">
                            <span className="font-semibold">
                              Deposit Amount
                            </span>
                            <div className="relative w-28">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                pattern="^\\d*\\.?\\d*$"
                                value={deposits[service.serviceId] ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*\.?\d*$/.test(value)) {
                                    setDeposits((prev) => ({
                                      ...prev,
                                      [service.serviceId]: value,
                                    }));
                                  }
                                }}
                                className="pl-5 pr-2 py-1.5 border rounded-md font-semibold w-full text-right text-sm focus:outline-primary"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Products and Packaging */}
          {!orderData?.products?.length ? (
            <div className="mt-3">
              <h3 className="text-lg font-semibold ml-2 mb-4">
                Products and Packaging
              </h3>
              <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                  No products selected! Go back and add products
                </h3>
                <div className="relative">
                  <button type="button" className="text-primary font-bold">
                    Select Products
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-lg">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold mt-3 ml-2">
                  Products and Packaging
                </h3>
                <button
                  type="button"
                  onClick={() => navigate(FrontendRoutes.CREATE_ORDER_PRODUCT)}
                  className="text-primary font-semibold"
                >
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
              </div>

              <div className="mt-4 rounded-lg">
                <div>
                  <div className="bg-quaternary p-2 rounded-lg">
                    {orderData?.products?.map((product) => {
                      const master = masterProducts.find(
                        (p) => p.id === product.productId,
                      );
                      return (
                        <div key={product.productId} className="mb-4">
                          <div className="p-4">
                            <div className="flex justify-between items-baseline mb-1">
                              <h4 className="font-semibold text-lg">
                                {master?.name}
                                {product.fatPercent > 0 &&
                                  `, ${product.fatPercent}% Fat`}
                              </h4>
                              {/* <span className="text-gray-900 font-bold text-xl">
                                ${master?.price}
                              </span> */}
                              {master && (
                                <span className="text-gray-900 font-bold text-xl">
                                  $
                                  {calculateTotalPrice(
                                    product.quantity,
                                    product.addOnIds,
                                    product.packaging.map((productPackage) => ({
                                      productPackageTypeId:
                                        productPackage.productPackageTypeId,
                                      packingPercentage:
                                        productPackage.percentage,
                                    })),
                                    master,
                                    addons,
                                    packageMasters,
                                  ).toFixed(2)}
                                </span>
                              )}
                            </div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-gray-600">
                                Total: {product.quantity}{' '}
                                {master?.productType == ProductType.QUANTITY
                                  ? 'Quantity'
                                  : WeightType.LBS}
                              </p>
                              {master && (
                                <span className="text-gray-800">
                                  $
                                  {(product.quantity * master?.price).toFixed(
                                    2,
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Add-ons */}
                          {product.addOnIds?.length > 0 && master && (
                            <div className="bg-white p-4 rounded-md border mb-4">
                              <h4 className="font-medium mb-2">
                                Add-ons:
                                {master.upchargeIngredientsFlag === 0 && (
                                  <span className="ml-2">(No Up-Charge)</span>
                                )}
                              </h4>
                              <div className="space-y-2">
                                {getSumOfAddOns(
                                  product.addOnIds,
                                  addons,
                                  product.quantity,
                                  master,
                                ).map((addon) => {
                                  if (!addon) return null; // skip if addons not present
                                  return (
                                    <div
                                      key={addon.id}
                                      className="flex justify-between"
                                    >
                                      <span className="w-1/2">
                                        {addon.name}
                                      </span>
                                      <span className="w-1/2 text-right">
                                        Total: ${addon.total.toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Packaging */}
                          {product.packaging?.length > 0 && master && (
                            <div className="bg-white p-4 rounded-md border mb-4">
                              <h4 className="font-medium mb-2 text-[#43474E]">
                                Packaging:
                              </h4>
                              <div className="space-y-2">
                                {/* Package Total */}
                                {getSumOfPackage(
                                  product.quantity,
                                  product.packaging.map((p) => ({
                                    productPackageTypeId:
                                      p.productPackageTypeId,
                                    packingPercentage: p.percentage,
                                  })),
                                  packageMasters,
                                  master,
                                ).map((packageTotal) => (
                                  <div
                                    key={packageTotal.packageId}
                                    className="flex justify-between text-sm text-gray-700"
                                  >
                                    <span className="w-1/4">
                                      {packageTotal.name}
                                    </span>
                                    <span className="w-1/4 text-center">
                                      Size: {packageTotal.size} {WeightType.LBS}
                                    </span>
                                    {product.packaging.length > 1 && (
                                      <span className="w-1/4">
                                        <span className="text-[#43474E]">
                                          Pkg Split:
                                        </span>{' '}
                                        {packageTotal.percentage}%
                                      </span>
                                    )}
                                    <span className="w-1/4 text-right">
                                      Total: ${packageTotal.total.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Packaging Notes */}
                          {product.packagingNotes && (
                            <div className="bg-white p-4 rounded-md border mb-4">
                              <h4 className="font-medium mb-2">
                                Packaging Notes:{' '}
                              </h4>
                              <div className="space-y-2">
                                <span className="text-[#43474E]">
                                  {product.packagingNotes}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Notes */}
          <div className="mt-2">
            <div className="flex justify-between items-center mb-2 mt-3">
              <h3 className="text-lg font-semibold ml-2">Additional Notes</h3>
            </div>
            <textarea
              placeholder="Add notes or description..."
              className="w-full border p-2 rounded-md h-25 focus:outline-primary"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between w-full gap-4 mt-4">
            <button
              onClick={() => history.back()}
              type="button"
              className="w-[50%] px-6 py-2 border border-primary text-primary bg-white hover:bg-primary hover:text-white rounded-sm"
            >
              Back
            </button>
            <button
              onClick={handleCompleteCustomization}
              type="button"
              className="w-[50%] px-6 py-2 bg-primary text-white hover:bg-dark-primary rounded-sm"
            >
              Complete Order Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
